import React, { useMemo, useState } from 'react';
import { Section, useAdminGet } from '../kit';
import { MODE_LABEL, Pill, type RecrawlCtx, RunLink, S, StatusPill, arr, fmtN, n0, num, obj, str } from './shared';

/**
 * What approving would change (engine: Projection / if_approved). Two counts, both "now → after":
 *  - the website: the live technologies table users browse (new and re-added rows are added, approved
 *    removals taken off; a moved technology stays one row under its new URL);
 *  - the master: keeps every technology ever listed — a removal is only marked, never deleted.
 * Every run that can still be approved has both scopes: "Approve all" and "Approve adds + modifications
 * only" (removals stay on the website).
 *
 *  - ProjectionBox: one run / one university (run drawer, university drawer).
 *  - IfApprovedCard: the Overview card — the runs waiting for approval, or every university's latest
 *    results, per university and in total.
 */

/** The auto-approve setting (GET settings): true / false, or null while it loads or if it failed. */
export const useAutoApprove = (): boolean | null => {
  const st = useAdminGet<any>(S, 'settings', {}, []);
  const v = obj(obj(st.data).settings).auto_approve;
  return typeof v === 'boolean' ? v : null;
};

/** How a dry run's results reach the website: through a full run, which stops for approval — unless
 *  auto-approve is on and no approval rule trips (then it goes live without an admin). */
export const fullRunApprovalText = (autoApprove: boolean | null | undefined, code?: string): string => {
  const via = `These changes reach the website through a full run${code ? ` of ${code}` : ''}`;
  if (autoApprove === true) return `${via}. Auto-approve is on: when no approval rule trips, that run’s changes go live without waiting for an admin.`;
  if (autoApprove === false) return `${via}, which stops for approval.`;
  return `${via}, which stops for approval unless auto-approve is on and no approval rule trips.`;
};

export type Scope = 'all' | 'adds_mods_only';
export const SCOPE_LABEL: Record<Scope, string> = { all: 'Approve all', adds_mods_only: 'Approve adds + modifications only' };

const signed = (v: any): string => {
  const n = n0(v);
  return n > 0 ? `+${n.toLocaleString()}` : n < 0 ? `−${Math.abs(n).toLocaleString()}` : '±0';
};

/** "+834 new · +21 back (re-added) · −4 removed · 15 moved (same row, new URL) · 29 updated in place"
 *  (short: "+834 new · +21 re-added · −4 removed · 15 moved · 29 updated", for table cells). */
export const websiteParts = (w: any, short = false): string[] => {
  const o = obj(w);
  const out: string[] = [];
  if (n0(o.new)) out.push(`+${fmtN(o.new)} new`);
  if (n0(o.readded)) out.push(short ? `+${fmtN(o.readded)} re-added` : `+${fmtN(o.readded)} back (re-added)`);
  if (n0(o.restored)) out.push(short ? `+${fmtN(o.restored)} restored` : `+${fmtN(o.restored)} restored (in the master, missing from the website)`);
  if (n0(o.removed)) out.push(`−${fmtN(o.removed)} removed`);
  if (n0(o.moved)) out.push(short ? `${fmtN(o.moved)} moved` : `${fmtN(o.moved)} moved (same row, new URL)`);
  if (n0(o.updated)) out.push(short ? `${fmtN(o.updated)} updated` : `${fmtN(o.updated)} updated in place`);
  if (n0(o.already_live)) out.push(short ? `${fmtN(o.already_live)} already live` : `${fmtN(o.already_live)} already on the website (updated)`);
  if (n0(o.removals_kept)) out.push(short ? `${fmtN(o.removals_kept)} removals kept` : `${fmtN(o.removals_kept)} removal${n0(o.removals_kept) === 1 ? '' : 's'} kept on the website`);
  return out;
};

const NowAfter: React.FC<{ now: any; after: any; big?: boolean }> = ({ now, after, big }) => {
  const d = n0(after) - n0(now);
  return (
    <span className={`inline-flex items-baseline gap-1.5 whitespace-nowrap ${big ? 'text-lg font-bold font-grotesk text-grayscale-1300' : ''}`}>
      <span>{fmtN(now)}</span><span className="text-grayscale-1100">→</span><span>{fmtN(after)}</span>
      <span className={`${big ? 'text-sm' : 'text-[11px]'} font-medium ${d > 0 ? 'text-green-700' : d < 0 ? 'text-red-700' : 'text-grayscale-1100'}`}>({signed(d)})</span>
    </span>
  );
};

/** Website + master numbers for one scope. */
export const ScopeNumbers: React.FC<{ s: any; big?: boolean }> = ({ s, big }) => {
  const w = obj(obj(s).website);
  const m = obj(obj(s).master);
  const parts = websiteParts(w);
  return (
    <div className="flex flex-col gap-2 min-w-0">
      <div className="flex flex-col gap-0.5">
        <div className="text-[11px] uppercase tracking-wide text-grayscale-1100">Website — what users see</div>
        <NowAfter now={w.now} after={w.after} big={big} />
        <div className="text-xs text-grayscale-1200">{parts.length ? parts.join(' · ') : 'no change'}</div>
      </div>
      <div className="flex flex-col gap-0.5">
        <div className="text-[11px] uppercase tracking-wide text-grayscale-1100">Master — keeps every technology</div>
        <div className="text-xs text-grayscale-1200 flex flex-col gap-0.5">
          <span>Rows <NowAfter now={obj(m.rows).now} after={obj(m.rows).after} /></span>
          <span>Listed (on the university sites) <NowAfter now={obj(m.listed).now} after={obj(m.listed).after} /></span>
          <span>Removed (kept, marked) <NowAfter now={obj(m.removed).now} after={obj(m.removed).after} /></span>
        </div>
      </div>
    </div>
  );
};

const sameBothWays = (p: any): boolean => {
  const sc = obj(obj(p).scopes);
  const a = obj(sc.all), b = obj(sc.adds_mods_only);
  return n0(obj(a.website).after) === n0(obj(b.website).after)
    && n0(obj(obj(a.master).listed).after) === n0(obj(obj(b.master).listed).after)
    && n0(obj(obj(a.master).removed).after) === n0(obj(obj(b.master).removed).after)
    && n0(obj(obj(a.master).missing).after) === n0(obj(obj(b.master).missing).after);
};

/** One run's / one university's numbers after approval, both scopes side by side when they differ. */
export const ProjectionBox: React.FC<{ p: any; ctx?: RecrawlCtx; title?: string; showRun?: boolean; note?: React.ReactNode }> =
  ({ p, ctx, title, showRun, note }) => {
  const o = obj(p);
  const sc = obj(o.scopes);
  const basis = str(o.basis);
  const same = sameBothWays(o);
  return (
    <div className="rounded-xl border border-grayscale-300 bg-white p-3 flex flex-col gap-2">
      <div className="flex items-center gap-2 flex-wrap">
        <span className="text-sm font-bold text-grayscale-1300 font-grotesk">{title || 'After approval'}</span>
        {showRun && ctx && num(o.run_id) !== null && (
          <span className="inline-flex items-center gap-1 text-xs text-grayscale-1200">
            from <RunLink ctx={ctx} id={o.run_id} /> <StatusPill status={o.status} />
            <span className="text-grayscale-1100">{MODE_LABEL[str(o.mode)] || str(o.mode)}</span>
          </span>
        )}
        {o.estimated && <Pill tone="amber" title="This run has totals but no per-technology rows: worked out from the totals">estimated</Pill>}
      </div>
      {note}
      {basis === 'none' ? (
        <div className="text-xs text-grayscale-1100">No numbers yet: this run stopped before it compared the website with the master.</div>
      ) : same ? (
        <>
          <ScopeNumbers s={sc.all} big />
          <div className="text-[11px] text-grayscale-1100">
            {n0(o.removals_proposed) ? 'The same with either approval.' : 'No removals proposed: the same with either approval.'}
          </div>
        </>
      ) : (
        <div className="grid gap-3 sm:grid-cols-2">
          {(['all', 'adds_mods_only'] as Scope[]).map(k => (
            <div key={k} className="flex flex-col gap-1.5 rounded-lg bg-grayscale-200/50 p-2.5 min-w-0">
              <div className="text-xs font-bold text-grayscale-1300">{SCOPE_LABEL[k]}</div>
              <ScopeNumbers s={sc[k]} />
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

/** "website 1,530 → 2,385 · master rows 1,614 → 2,448" for one scope (the approve dialog). */
export const projectionLine = (p: any, scope: Scope): string => {
  const s = obj(obj(obj(p).scopes)[scope]);
  const w = obj(s.website);
  const rows = obj(obj(s.master).rows);
  if (!Object.keys(w).length || str(obj(p).basis) === 'none') return '';
  return `website ${fmtN(w.now)} → ${fmtN(w.after)} · master ${fmtN(rows.now)} → ${fmtN(rows.after)} rows`;
};

// ---------------------------------------------------------------------------
// The Overview card
// ---------------------------------------------------------------------------
export const IfApprovedCard: React.FC<{ data: any; ctx: RecrawlCtx; autoApprove?: boolean | null }> = ({ data, ctx, autoApprove }) => {
  const d = obj(data);
  const pending = obj(d.pending);
  const latest = obj(d.latest);
  const nPending = arr(pending.runs).length;
  const [view, setView] = useState<'pending' | 'latest'>(nPending ? 'pending' : 'latest');
  const [scope, setScope] = useState<Scope>('all');
  const block = view === 'pending' ? pending : latest;
  const runs = view === 'pending' ? arr(pending.runs) : arr(latest.universities);
  const tot = obj(obj(block.totals)[scope]);
  const rows = useMemo(() => runs.slice().sort((a, b) =>
    Math.abs(n0(obj(obj(obj(b.scopes)[scope]).website).delta)) - Math.abs(n0(obj(obj(obj(a.scopes)[scope]).website).delta))
    || str(a.code).localeCompare(str(b.code))), [runs, scope]);
  const anyRemovals = runs.some(p => n0(obj(p).removals_proposed) > 0);
  const without = arr(latest.without).map(str);

  const tab = (k: 'pending' | 'latest', label: string) => (
    <button type="button" onClick={() => setView(k)}
      className={`text-xs rounded-full border px-2.5 py-1 ${view === k ? 'bg-primary-500 border-primary-500 text-white' : 'border-grayscale-500 text-grayscale-1200 hover:bg-grayscale-200'}`}>
      {label}
    </button>
  );
  return (
    <Section title="If approved"
      right={
        <div className="flex items-center gap-1.5 flex-wrap">
          {tab('pending', `Waiting for approval (${nPending})`)}
          {tab('latest', `Every university’s latest results (${arr(latest.universities).length})`)}
          <span className="w-2" />
          {(['all', 'adds_mods_only'] as Scope[]).map(k => (
            <button key={k} type="button" onClick={() => setScope(k)}
              className={`text-xs rounded-full border px-2.5 py-1 ${scope === k ? 'bg-grayscale-1300 border-grayscale-1300 text-white' : 'border-grayscale-500 text-grayscale-1200 hover:bg-grayscale-200'}`}>
              {k === 'all' ? 'All changes' : 'Adds + modifications only'}
            </button>
          ))}
        </div>
      }>
      <div className="text-xs text-grayscale-1100">
        {view === 'pending'
          ? 'The final counts once the runs waiting for approval are approved.'
          : `The final counts if every university’s latest results were approved — a dry run’s results apply only through a full run, ${autoApprove === true
            ? 'and auto-approve is on: a full run where no approval rule trips goes live without an admin.'
            : autoApprove === false ? 'which stops for approval.' : 'which stops for approval unless auto-approve is on and no approval rule trips.'}`}
        {' '}The master never deletes: a removal is only marked.
        {scope === 'adds_mods_only' && anyRemovals ? ' With adds + modifications only, proposed removals stay on the website.' : ''}
      </div>
      {!runs.length ? (
        <div className="text-sm text-grayscale-1100">
          {view === 'pending' ? 'No run is waiting for approval.' : 'No university has results waiting to be applied.'}
        </div>
      ) : (
        <>
          <div className="grid gap-3 md:grid-cols-2">
            <div className="rounded-lg bg-grayscale-200/50 p-3 flex flex-col gap-1">
              <div className="text-[11px] uppercase tracking-wide text-grayscale-1100">Website — what users see</div>
              <NowAfter now={obj(tot.website).now} after={obj(tot.website).after} big />
              <div className="text-xs text-grayscale-1200">{websiteParts(tot.website).join(' · ') || 'no change'}</div>
            </div>
            <div className="rounded-lg bg-grayscale-200/50 p-3 flex flex-col gap-1">
              <div className="text-[11px] uppercase tracking-wide text-grayscale-1100">Master — keeps every technology (rows)</div>
              <NowAfter now={obj(obj(tot.master).rows).now} after={obj(obj(tot.master).rows).after} big />
              <div className="text-xs text-grayscale-1200 flex flex-wrap gap-x-3">
                <span>Listed on the university sites <NowAfter now={obj(obj(tot.master).listed).now} after={obj(obj(tot.master).listed).after} /></span>
                <span>Removed (kept, marked) <NowAfter now={obj(obj(tot.master).removed).now} after={obj(obj(tot.master).removed).after} /></span>
              </div>
            </div>
          </div>
          <div className="overflow-auto rounded-lg border border-grayscale-300 max-h-[26rem]">
            <table className="w-full text-[12.5px]">
              <thead className="bg-grayscale-200 text-[11px] text-grayscale-1200 sticky top-0">
                <tr>{['University', 'From run', 'Website now → after', 'Changes on the website', 'Master: rows · removed (kept)'].map(h =>
                  <th key={h} className="text-left px-2 py-1 whitespace-nowrap">{h}</th>)}</tr>
              </thead>
              <tbody>
                {rows.map(p => {
                  const s = obj(obj(p.scopes)[scope]);
                  const w = obj(s.website);
                  const m = obj(s.master);
                  return (
                    <tr key={str(p.run_id)} className="border-t border-grayscale-200 align-top hover:bg-primary-100/30 cursor-pointer"
                      onClick={() => ctx.openRun(str(p.run_id))} title="Open the run">
                      <td className="px-2 py-1"><b>{str(p.code)}</b> <span className="text-grayscale-1100 text-xs block max-w-[11rem] truncate" title={str(p.name)}>{str(p.name) !== str(p.code) ? str(p.name) : ''}</span></td>
                      <td className="px-2 py-1 whitespace-nowrap">
                        <span className="inline-flex items-center gap-1">#{str(p.run_id)} <StatusPill status={p.status} />
                          <span className="text-[11px] text-grayscale-1100">{MODE_LABEL[str(p.mode)] || str(p.mode)}</span>
                          {p.estimated && <Pill tone="amber">estimated</Pill>}</span>
                      </td>
                      <td className="px-2 py-1">{str(p.basis) === 'none' ? <span className="text-grayscale-1100">no numbers yet</span> : <NowAfter now={w.now} after={w.after} />}</td>
                      <td className="px-2 py-1 text-xs text-grayscale-1200 min-w-[12rem]" title={websiteParts(w).join('\n')}>{websiteParts(w, true).join(' · ') || '—'}</td>
                      <td className="px-2 py-1 text-xs">
                        <span className="block"><NowAfter now={obj(m.rows).now} after={obj(m.rows).after} /></span>
                        <span className="block text-grayscale-1100">removed <NowAfter now={obj(m.removed).now} after={obj(m.removed).after} /></span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </>
      )}
      {view === 'latest' && without.length > 0 && (
        <div className="text-[11px] text-grayscale-1100">
          Nothing waiting for: {without.map((c, i) => (
            <React.Fragment key={c}>{i ? ', ' : ''}<button type="button" className="underline underline-offset-2" onClick={() => ctx.openUniversity(c)}>{c}</button></React.Fragment>
          ))} (no finished dry run or run waiting for approval, or its latest results are already applied).
        </div>
      )}
    </Section>
  );
};
